import { Availability } from "@nuorder/client-cart-api/dist/types/modules/availability/models";
import { Warehouse } from "@nuorder/client-cart-api/dist/types/modules/brand/models";
import { LineItem } from "@nuorder/client-cart-api/dist/types/modules/order/models";
import { Inventory } from "components/shipment-review/types";
import { AllocationType } from "types/cart";
import { ICustomizationData } from "constants/product-customizations/customization";
import { SchemaFieldSubtype } from "types/schema";
import type { IProduct, IProductSize } from "types/product";

export interface IDeliveryRange {
    shipStart: Date;
    shipEnd: Date;
}

export interface IDateRange extends IDeliveryRange {
    dateKey: string;
}

export interface ICurrency {
    code: string;
    symbol: string;
    decimalPlaces: number;
}

export interface IOrderCurrency {
    wholesale: ICurrency;
    retail: ICurrency;
}

export interface IBulkUnits {
    units: number;
    sized: number;
    wholesale: number;
    retail: number;
    unitsPerPack: number;
}

export interface ISizeHeaderPrices {
    prices: string[];
    discount: boolean;
}

export interface IDetailHeaderPrices {
    [size: string]: ISizeHeaderPrices;
}

export interface IGridEntryPricing {
    currency: IOrderCurrency;
    discount: number;
    wholesale: number;
    original_wholesale: number;
    adjusted_wholesale: number;
    discounted_price: number;
    retail: number;
    // contains promotional prices for all product sizes
    wholesale_range: [number, number];
    // contains original prices for all product sizes
    original_wholesale_range: [number, number];
}

export interface IGridEntry
    extends Omit<
        LineItem,
        | "price"
        | "prices"
        | "originalPrice"
        | "originalPrices"
        | "priceOverride"
        | "discountPercentOverride"
        | "totalPrice"
        | "totalPrices"
        | "attributes"
        | "productVariant"
    > {
    shipStart: Date;
    shipEnd: Date;
    location: ILocation | null;
    bulkUnits: IBulkUnits | null;
    allocated: AllocationType | null;
    defaultWarehouseId: string;
    availableWarehouses: Warehouse[];
    pricing: IGridEntryPricing;
    inventory: Inventory;
    isOverordered?: boolean;
    hasError?: boolean;
    customization?: ICustomizationData;
    product: IProduct;
    size: IProductSize;
    attributes?: LineItemAttributes;
}

type ValueBySubtypeMap = {
    [SchemaFieldSubtype.Date]: string;
    [SchemaFieldSubtype.String]: string | string[];
    [SchemaFieldSubtype.LongString]: string;
    [SchemaFieldSubtype.Lookup]: string | string[];
};

export interface FdmAttribute {
    field: string;
    type: SchemaFieldSubtype;
    value: ValueBySubtypeMap[SchemaFieldSubtype];
    lookupValue: null | string | string[];
    lookup?: string;
    hasError?: boolean;
}

export interface LineItemAttributes {
    [field: string]: FdmAttribute;
}

export enum VIEW_MODE {
    INITIAL = "initial",
    SHIPMENT = "shipment",
}

export interface ILocation {
    id: string | null; // null for combined allocations
    name: string;
}

export interface IAvailability {
    [warehouseId: string]: {
        [productId: string]: Availability;
    };
}

export interface IOrderData {
    uniqueDates: IDateRange[]; // all unique delivery windows to display in tables
    shipments: IDateRange[]; // list of build order shipments
    currency: IOrderCurrency;
    entries: IGridEntry[];
    deliveryConfig: {
        enforceAvailableUntil: boolean;
        dbpPriceEnabled: boolean;
        minimumDeliveryWindow: number;
        defaultDeliveryWindow: number;
        cumulativeDate: CumulativeDate;
        allocateInventoryAllLocationsCombined: boolean;
        holdInventory: boolean;
        hideBulkUnits: boolean;
    };
    useBulk?: boolean;
    availability: IAvailability;
    selectedLocations?: ILocation[];
}

export type CumulativeDate = "shipStart" | "shipEnd";
